import { useState } from 'react';

import classnames from 'classnames';
import Global from '../../store/global-selectors';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';

import ShipToContainer from '../ship-to';
import { RecipientTaxInfoContent } from '../tax-info/recipient-tax-info-content';
import SoldToComponent from './sold-to-component';
import EditShipToLink from './edit-ship-to-link';
import { ComponentStates } from '../../enums/component-states';
import { useIsFedexHALBySalesOrderId } from '../../hooks/orders/use-fedex-hal-check';
import { usePickupRelayOrderInfoBySalesOrderId } from '../../hooks/orders/use-pickup-relay-order-check';
import { useSalesOrderRestrictionsBySOId } from '../../hooks/entities/selectors/sales-orders';
import PickupRelayTooltip from './pickup-relay-tooltip';
import { testId } from 'ui-components/services/testid-service';

import style from './customer-recipient-widget.module.less';

export const customerMessages = defineMessages({
  header: {
    description: 'Header text for the recipient information section',
    defaultMessage: 'Recipient',
    id: 'Hyo59P',
  },
  pudoHeader: {
    description: 'Header text for the recipient information section for PickupRelay orders',
    defaultMessage: 'Pickup Relay Location',
    id: 'EXZGSU',
  },
  shipTo: {
    description: 'shipTo.shipTo',
    defaultMessage: 'Ship To',
    id: 'Y5a024',
  },
});

export const displayHeader = (isPickupRelayOrder) => {
  const headerText = isPickupRelayOrder ? (
    <>
      <FormattedMessage {...customerMessages.pudoHeader} />
      <PickupRelayTooltip tooltipTargetWrapperClassName={style.pickupRelayTooltipWrapper} />
    </>
  ) : (
    <FormattedMessage {...customerMessages.header} />
  );
  return headerText;
};

/**
 * @param {object} props
 * @param {string} props.salesOrderId
 * @param {string} props.selectedFulfillmentPlanId
 * @param {boolean} [props.showHeader]
 * @param {string} [props.headerClassName]
 * @param {string} [props.editLinkContainerClassName]
 * @returns {JSX.Element}
 */
const CustomerRecipientWidget = ({
  salesOrderId,
  selectedFulfillmentPlanId,
  showHeader = false,
  headerClassName,
  editLinkContainerClassName,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const disableEdit = useIsFedexHALBySalesOrderId(salesOrderId);
  const isPickupRelayOrder = usePickupRelayOrderInfoBySalesOrderId(salesOrderId) !== null;

  const soldTo = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'soldTo',
    }),
  );

  const toggleEditMode = () => setIsEdit(!isEdit);
  const uiRestrictions = useSalesOrderRestrictionsBySOId(salesOrderId);
  const readonlyShipToAddress = uiRestrictions?.shipTos?.componentState === ComponentStates.ReadOnly;

  if (!selectedFulfillmentPlanId || !salesOrderId) {
    return null;
  }

  const headerClassNames = classnames(
    {
      [style['top-row']]: showHeader,
      [style['no-header-top-row']]: !showHeader,
    },
    headerClassName,
  );

  const shouldShowEditLink = !isEdit && !isPickupRelayOrder && !readonlyShipToAddress;

  return (
    <div className={style['customer-widget-wrapper']} {...testId('customer-recipient-widget')}>
      <div className={headerClassNames}>
        {showHeader && displayHeader(isPickupRelayOrder)}
        {shouldShowEditLink && (
          <EditShipToLink
            disableEdit={disableEdit}
            linkContainerClassName={editLinkContainerClassName}
            setEditMode={toggleEditMode}
            showHeader={showHeader}
          />
        )}
      </div>
      <div className={style['ship-to']}>
        <div className={isEdit ? style['edit-shipto-label'] : style['shipto-label']}>
          <FormattedMessage {...customerMessages.shipTo} />
        </div>
        <ShipToContainer
          addressWrapperClassName={style['shipto-address-wrapper']}
          className={style['shipto-wrapper']}
          editAddressWrapperClassName={style['edit-shipto-address']}
          isEdit={isEdit}
          isPickupRelayOrder={isPickupRelayOrder}
          notifyParent={toggleEditMode}
          salesOrderId={salesOrderId}
          selectedFulfillmentPlanId={selectedFulfillmentPlanId}
        />
      </div>
      {!isEdit && <RecipientTaxInfoContent fulfillmentPlanId={selectedFulfillmentPlanId} salesOrderId={salesOrderId} />}
      {!isEdit && <SoldToComponent soldTo={soldTo} />}
    </div>
  );
};

export default CustomerRecipientWidget;
